import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';

import { Col } from '@opendoor/atoms/box';
import { usePendingOAuthAccountCreatedEvent } from 'components/account/usePendingOAuthAccountCreatedEvent';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import { buildGalleryPhotos } from 'components/marketplace/helpers/galleryPhotos';
import { dollarsToCents } from 'components/marketplace/helpers/money';
import { useMarketplaceFavorite } from 'components/marketplace/helpers/useMarketplaceFavorite';
import { rememberViewedProperty } from 'components/marketplace/helpers/viewedProperties';
import { propertyMapSearch } from 'components/marketplace/map/refresh/propertyMapSearch';
import { mapRefreshForRequest } from 'components/marketplace/map/refresh/release';
import { loadPropertyPage } from 'components/marketplace/property/details/loadPropertyPage';
import type { PropertyPageProps } from 'components/marketplace/property/details/loadPropertyPage';
import { GalleryPhotos } from 'components/marketplace/property/media/GalleryPhotos';
import {
  heroWalkthroughVideoUrl,
  pdpHeroMedia,
  pdpMediaAvailability,
  shouldMountHeroTour3d,
} from 'components/marketplace/property/media/pdpEnhancedMedia';
import { PhotoCarousel } from 'components/marketplace/property/media/PhotoCarousel';
import { usePdpMediaSlot } from 'components/marketplace/property/media/usePdpMediaSlot';
import { PdpAnalytics } from 'components/marketplace/property/PdpAnalytics';
import { PropertySection } from 'components/marketplace/property/PropertySection';
import { PdpRefreshExposure } from 'components/marketplace/property/refresh/PdpRefreshExposure';
import {
  headerValue,
  PDP_REFRESH_ARM_HEADER,
  PDP_REFRESH_VARIATION_HEADER,
  pdpRefreshFromEdge,
} from 'components/marketplace/property/refresh/release';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import { StagedPhotosExposure } from 'components/marketplace/stagedPhotos/StagedPhotosExposure';
import MarketplaceLayout from 'components/properties/shared/MarketplaceLayout';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import dynamic from 'next/dynamic';

import { useObservability } from '../../../../helpers/observability';

const Lightbox = dynamic(
  () => import('components/marketplace/property/Lightbox').then((mod) => mod.Lightbox),
  {
    ssr: false,
  },
);

const PdpRefresh = dynamic(() =>
  import('components/marketplace/property/refresh/PdpRefresh').then((module) => module.PdpRefresh),
);

const ShareModal = dynamic(
  () => import('components/marketplace/property/ShareModal').then((mod) => mod.ShareModal),
  {
    ssr: false,
  },
);

const HomesPage = lazy(() => import('pages/homes/[market]'));

const subscribeToHydration = () => () => {};
const clientHydrated = () => true;
const serverHydrated = () => false;
// React 18 drops boolean inert attributes from JSX.
const disableFallbackInteractions = (element: HTMLDivElement | null) => {
  element?.setAttribute('inert', '');
};

type PageProps = PropertyPageProps & { mapRefreshEnabled?: boolean };

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const hydrated = useSyncExternalStore(subscribeToHydration, clientHydrated, serverHydrated);
  const property = props.property;
  usePendingOAuthAccountCreatedEvent({
    addressUuid: props.preview ? null : property.addressToken,
    cookiePath: '/properties',
  });
  const mapSearch = useMemo(
    () => (props.mapRefreshEnabled ? propertyMapSearch(property) : null),
    [props.mapRefreshEnabled, property],
  );
  const content = (
    <PageInner
      {...props}
      preview={Boolean(mapSearch) || (props.preview ?? false)}
      interactive={!mapSearch}
    />
  );
  // Preserve the listing HTML without accepting actions that the map handoff would discard.
  const propertyPage = (
    <PdpAnalytics
      addressId={property.addressToken}
      refreshed={props.pdpRefreshEnabled}
      preview={Boolean(mapSearch) || (props.preview ?? false)}
    >
      <PdpRefreshExposure
        addressId={property.addressToken}
        experiment={props.pdpRefreshExperiment}
        preview={Boolean(mapSearch) || (props.preview ?? false)}
      />
      <StagedPhotosExposure exposure={props.stagedPhotosExposure} />
      {mapSearch ? (
        <>
          <div role="status" className="sr-only">
            Loading property map
          </div>
          <div ref={disableFallbackInteractions}>{content}</div>
        </>
      ) : (
        content
      )}
    </PdpAnalytics>
  );
  return mapSearch && hydrated ? (
    <Suspense fallback={propertyPage}>
      <HomesPage key={property.addressToken} {...mapSearch} initialPropertyPage={props} />
    </Suspense>
  ) : (
    propertyPage
  );
};

const PageInner: React.FC<
  InferGetServerSidePropsType<typeof getServerSideProps> & { interactive?: boolean }
> = ({
  property,
  pdpRefreshEnabled,
  engagement,
  preview = false,
  interactive = true,
  mapRefreshEnabled = false,
}) => {
  const { trackJourneyEvent } = useObservability();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [shareModalState, setShareModalState] = useState<'unopened' | 'open' | 'closed'>(
    'unopened',
  );
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const toggleLightbox = useCallback(() => setIsLightboxOpen((prev) => !prev), []);
  // bricks-next `Lightbox` registers a document-level keydown listener that
  // fires `handleClose()` on Escape regardless of `isOpen`, so `handleClose`
  // must be close-only rather than a toggle.
  const closeLightbox = useCallback(() => setIsLightboxOpen(false), []);
  const handleSetSelectedPhotoIndex = useCallback((idx: number) => setSelectedPhotoIndex(idx), []);
  const { isFavoriteLoading, isLiked, onToggleFavorite } = useMarketplaceFavorite({
    addressId: property.addressToken,
    origin: 'LISTING_DETAILS',
    priceCents: property.listPrice == null ? null : dollarsToCents(property.listPrice),
  });
  const handleToggleFavorite = useCallback(() => {
    onToggleFavorite();
  }, [onToggleFavorite]);
  const marketplaceProperty = useMemo<PropertyDetailsType>(
    () => ({
      ...property,
      isFavoriteLoading,
      isLiked,
      onToggleFavorite: handleToggleFavorite,
    }),
    [handleToggleFavorite, isFavoriteLoading, isLiked, property],
  );

  const formattedAddress = useMemo(
    () => formatStreetAddress(property.street, property.unitType, property.unitNumber),
    [property.street, property.unitType, property.unitNumber],
  );

  const carouselPhotos = useMemo(() => buildGalleryPhotos(property), [property]);
  const openShareModal = useCallback(() => setShareModalState('open'), []);
  const closeShareModal = useCallback(() => setShareModalState('closed'), []);
  const walkthroughVideoUrl = useMemo(() => heroWalkthroughVideoUrl(property), [property]);
  const availability = useMemo(
    () =>
      pdpMediaAvailability(preview ? null : walkthroughVideoUrl, preview ? null : property.tour3d),
    [preview, property.tour3d, walkthroughVideoUrl],
  );
  const mediaSlot = usePdpMediaSlot({ addressToken: property.addressToken, availability });
  const showTour3d = shouldMountHeroTour3d(property.tour3d, mediaSlot.assignedState);
  const displayTour3d = showTour3d || preview;
  const galleryTour3dUrl = pdpRefreshEnabled && displayTour3d ? property.tour3d?.url : undefined;
  const heroMediaFill = useMemo(
    () =>
      pdpHeroMedia(property, mediaSlot, pdpRefreshEnabled ? 'gallery-pill' : 'fill', {
        galleryTour3dUrl,
      }),
    [galleryTour3dUrl, mediaSlot, pdpRefreshEnabled, property],
  );
  const heroMediaControl = useMemo(
    () => pdpHeroMedia(property, mediaSlot, 'control'),
    [mediaSlot, property],
  );

  useEffect(() => {
    if (preview) return;
    // Only the refreshed map shows viewed homes, so only it remembers them.
    if (mapRefreshEnabled) rememberViewedProperty(property.addressToken);
    trackJourneyEvent('marketplaceProperty', 'detailPageView', {
      addressUuid: property.addressToken,
    });
  }, [mapRefreshEnabled, preview, property.addressToken, trackJourneyEvent]);

  return (
    <MarketplaceLayout property={marketplaceProperty} fullPageScroll={pdpRefreshEnabled}>
      {pdpRefreshEnabled ? (
        <PdpRefresh
          property={marketplaceProperty}
          engagement={engagement}
          heroOverlay={heroMediaFill}
          showTour3d={displayTour3d && !heroMediaFill}
          preview={preview}
          interactive={interactive}
        />
      ) : (
        <Col className="w-screen overflow-hidden max-md:overflow-y-scroll md:flex-row md:h-[calc(100vh-var(--site-header-height)-var(--site-promo-banner-height,0px))]">
          <GalleryPhotos
            handleSetSelectedPhotoIndex={handleSetSelectedPhotoIndex}
            property={marketplaceProperty}
            toggleLightbox={toggleLightbox}
            heroOverlay={heroMediaFill}
          />
          <PhotoCarousel
            isAvailableSoon={marketplaceProperty.listingState === 'AVAILABLE_SOON'}
            photos={carouselPhotos}
            price={getDisplayPrice(marketplaceProperty, 'purchase')}
            address={formattedAddress}
            toggleLightbox={toggleLightbox}
            setSelectedPhotoIndex={handleSetSelectedPhotoIndex}
            latitude={marketplaceProperty.latitude}
            longitude={marketplaceProperty.longitude}
            openShareModal={openShareModal}
            heroOverlay={heroMediaControl}
          />
          <PropertySection property={marketplaceProperty} />
        </Col>
      )}
      {isLightboxOpen && (
        <Lightbox
          handleClose={closeLightbox}
          isOpen={isLightboxOpen}
          property={marketplaceProperty}
          selectedPhotoIndex={selectedPhotoIndex}
          priceQuote={pdpRefreshEnabled ? 'listed' : 'purchase'}
        />
      )}
      {!pdpRefreshEnabled && shareModalState !== 'unopened' && (
        <ShareModal
          property={marketplaceProperty}
          isOpen={shareModalState === 'open'}
          onClose={closeShareModal}
        />
      )}
    </MarketplaceLayout>
  );
};

export const getServerSideProps: CacheSafeGetServerSideProps<PageProps> = async (context) => {
  const mapRefreshEnabled = mapRefreshForRequest(process.env.COSMOS_MAP_REFRESH_ENABLED, context);
  const decision = pdpRefreshFromEdge({
    releaseValue: process.env.COSMOS_PDP_REFRESH_ENABLED,
    armHeader: headerValue(context.req.headers[PDP_REFRESH_ARM_HEADER]),
    variationHeader: headerValue(context.req.headers[PDP_REFRESH_VARIATION_HEADER]),
  });
  const result = await loadPropertyPage({
    context,
    pdpRefreshEnabled: mapRefreshEnabled || decision.enabled,
    pdpRefreshExperiment: mapRefreshEnabled ? null : decision.experiment,
  });
  if (!mapRefreshEnabled || !('props' in result)) return result;
  return { props: { ...(await result.props), mapRefreshEnabled } };
};

export default Page;
